import { useHttp } from '@veda/dashboard-ui';

// useHttp espone: request(url, options)
// options: { method, body, onSuccess, onError, onFormError, type }

const BASE = '/api/v1/ppp';

export function usePppApi() {
  const { request, loading } = useHttp();

  const get = (url, callbacks = {}) =>
    request(url, { method: 'GET', ...callbacks });

  const post = (url, data, callbacks = {}) =>
    request(url, { method: 'POST', body: JSON.stringify(data), ...callbacks });

  const put = (url, data, callbacks = {}) =>
    request(url, { method: 'PUT', body: JSON.stringify(data), ...callbacks });

  const patch = (url, data, callbacks = {}) =>
    request(url, { method: 'PATCH', body: JSON.stringify(data), ...callbacks });

  const del = (url, callbacks = {}) =>
    request(url, { method: 'DELETE', ...callbacks });

  return {
    loading,

    // Procedimenti
    listProcedimenti:      (cb) => get(`${BASE}/procedimenti`, cb),
    getProcedimento:       (id, cb) => get(`${BASE}/procedimenti/${id}`, cb),
    creaProcedimento:      (data, cb) => post(`${BASE}/procedimenti`, data, cb),
    aggiornaProcedimento:  (id, data, cb) => put(`${BASE}/procedimenti/${id}`, data, cb),
    eliminaProcedimento:   (id, cb) => del(`${BASE}/procedimenti/${id}`, cb),

    // Fasi
    listFasi:   (procedimentoId, cb) => get(`${BASE}/procedimenti/${procedimentoId}/fasi`, cb),
    avanzaFase: (procedimentoId, faseId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/fasi/${faseId}/avanza`, {}, cb),
    retrocediFase: (procedimentoId, faseId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/fasi/${faseId}/retrocedi`, {}, cb),

    // Proposte
    listProposte: (procedimentoId, params, cb) => {
      if (procedimentoId) {
        return get(`${BASE}/procedimenti/${procedimentoId}/proposte`, cb);
      }
      const qs = params?.procedimentoId ? `?procedimentoId=${params.procedimentoId}` : '';
      return get(`${BASE}/proposte${qs}`, cb);
    },
    getProposta:      (procedimentoId, propostaId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}`, cb),
    getPropostaDiretta: (propostaId, cb) =>
      get(`${BASE}/proposte/${propostaId}`, cb),
    urlRicevutaPdf: (propostaId) =>
      `${process.env.REACT_APP_API_URL ?? ''}${BASE}/proposte/${propostaId}/ricevuta`,
    registraProposta: (data, cb) =>
      post(`${BASE}/procedimenti/${data.procedimentoId}/proposte`, data, cb),
    creaProposte:  (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte`, data, cb),
    valutaProposta: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/valuta`, data, cb),
    aggiornaValutazioneInteressePubblico: (procedimentoId, propostaId, data, cb) =>
      request(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/valutazione-interesse-pubblico`, {
        method: 'PATCH', body: JSON.stringify(data), ...cb,
      }),
    dichiaraInteressePubblico: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/dichiara-interesse-pubblico`, data, cb),

    // Fase 7 — Pubblicazione proposta di interesse pubblico (art. 193, co. 4)
    pubblicaInteressePubblico: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/pubblica-interesse-pubblico`, data, cb),
    listaConcorrenti: (procedimentoId, propostaId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/concorrenti`, cb),
    registraConcorrente: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/concorrenti`, data, cb),

    // Operatori economici
    listOperatoriEconomici: (cb) => get(`${BASE}/operatori-economici`, cb),

    // Documenti
    listDocumenti:    (procedimentoId, params, cb) => {
      const qs = params ? '?' + new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v]) => v != null))).toString() : '';
      return get(`${BASE}/procedimenti/${procedimentoId}/documenti${qs}`, cb);
    },
    getDocumento:     (procedimentoId, documentoId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/documenti/${documentoId}`, cb),
    uploadDocumento:  (procedimentoId, formData, cb) =>
      request(`${BASE}/procedimenti/${procedimentoId}/documenti`, {
        method: 'POST', body: formData, type: 'file_upload', ...cb,
      }),
    scaricaDocumento: (procedimentoId, documentoId) =>
      `${BASE}/procedimenti/${procedimentoId}/documenti/${documentoId}/download`,
    scaricaZipDocumenti: (procedimentoId, faseId) =>
      faseId
        ? `${BASE}/procedimenti/${procedimentoId}/documenti/zip?faseId=${faseId}`
        : `${BASE}/procedimenti/${procedimentoId}/documenti/zip`,
    cambiaStatoDocumento: (procedimentoId, documentoId, stato, cb) =>
      request(`${BASE}/procedimenti/${procedimentoId}/documenti/${documentoId}/stato`, {
        method: 'PATCH', body: JSON.stringify({ stato }), ...cb,
      }),
    nuovaVersioneDocumento: (procedimentoId, documentoId, formData, cb) =>
      request(`${BASE}/procedimenti/${procedimentoId}/documenti/${documentoId}/versione`, {
        method: 'POST', body: formData, type: 'file_upload', ...cb,
      }),
    eliminaDocumento: (procedimentoId, documentoId, cb) =>
      del(`${BASE}/procedimenti/${procedimentoId}/documenti/${documentoId}`, cb),
    checklistFase: (procedimentoId, faseId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/fasi/${faseId}/checklist-documenti`, cb),

    // Scadenze
    listScadenze:     (procedimentoId, cb) => get(`${BASE}/procedimenti/${procedimentoId}/scadenze`, cb),
    prorogaScadenza:  (procedimentoId, scadenzaId, data, cb) => {
      const formData = new FormData();
      formData.append('nuovaData', data.nuovaData);
      formData.append('motivazione', data.motivazione);
      if (data.allegato) formData.append('allegato', data.allegato);
      return request(`${BASE}/procedimenti/${procedimentoId}/scadenze/${scadenzaId}/proroga`, {
        method: 'POST', body: formData, type: 'file_upload', ...cb,
      });
    },
    downloadAllegatoProroga: (procedimentoId, scadenzaId, prorogaId) =>
      `${BASE}/procedimenti/${procedimentoId}/scadenze/${scadenzaId}/proroghe/${prorogaId}/allegato`,
    aggiornaAlert:    (procedimentoId, scadenzaId, data, cb) =>
      request(`${BASE}/procedimenti/${procedimentoId}/scadenze/${scadenzaId}/alert`, {
        method: 'PATCH', body: JSON.stringify(data), ...cb,
      }),
    chiudiScadenza:   (procedimentoId, scadenzaId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/scadenze/${scadenzaId}/chiudi`, {}, cb),
    reportRitardiRup: (cb) => get(`${BASE}/report/ritardi-rup`, cb),

    // Reportistica PPP (task #86c9nq8hu)
    getReportAvanzamento: (procedimentoId, cb) =>
      get(`${BASE}/report/procedimenti/${procedimentoId}/avanzamento`, cb),
    urlReportAvanzamentoPdf: (procedimentoId) =>
      `${BASE}/report/procedimenti/${procedimentoId}/avanzamento/pdf`,
    urlReportAvanzamentoExcel: (procedimentoId) =>
      `${BASE}/report/procedimenti/${procedimentoId}/avanzamento/excel`,
    getReportTermini: (procedimentoId, cb) =>
      get(`${BASE}/report/procedimenti/${procedimentoId}/termini`, cb),
    urlReportTerminiPdf: (procedimentoId) =>
      `${BASE}/report/procedimenti/${procedimentoId}/termini/pdf`,
    urlReportTerminiExcel: (procedimentoId) =>
      `${BASE}/report/procedimenti/${procedimentoId}/termini/excel`,
    getReportPerformanceRup: (rupId, cb) => {
      const qs = rupId ? `?rup_id=${rupId}` : '';
      return get(`${BASE}/report/performance-rup${qs}`, cb);
    },
    urlReportPerformanceRupPdf: (rupId) =>
      `${BASE}/report/performance-rup/pdf${rupId ? `?rup_id=${rupId}` : ''}`,
    getReportPubblicazioni: (procedimentoId, cb) =>
      get(`${BASE}/report/procedimenti/${procedimentoId}/pubblicazioni`, cb),
    getStatisticheAggregate: (params, cb) => {
      const qs = new URLSearchParams();
      if (params?.periodo_inizio) qs.append('periodo_inizio', params.periodo_inizio);
      if (params?.periodo_fine)   qs.append('periodo_fine',   params.periodo_fine);
      if (params?.rup_id)         qs.append('rup_id',         params.rup_id);
      if (params?.stato)          qs.append('stato',          params.stato);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      return get(`${BASE}/report/statistiche${q}`, cb);
    },
    urlStatisticheExcel: (params) => {
      const qs = new URLSearchParams();
      if (params?.periodo_inizio) qs.append('periodo_inizio', params.periodo_inizio);
      if (params?.periodo_fine)   qs.append('periodo_fine',   params.periodo_fine);
      if (params?.rup_id)         qs.append('rup_id',         params.rup_id);
      if (params?.stato)          qs.append('stato',          params.stato);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      return `${BASE}/report/statistiche/excel${q}`;
    },
    listUtentiRup: (enteId, cb) => {
      if (typeof enteId === 'object') { cb = enteId; enteId = undefined; }
      const q = enteId != null ? `?enteId=${enteId}` : '';
      return get(`${BASE}/utenti/rup${q}`, cb);
    },

    // Atti
    listAtti:    (procedimentoId, cb) => get(`${BASE}/procedimenti/${procedimentoId}/atti`, cb),
    creaAtto:    (procedimentoId, data, cb) => post(`${BASE}/procedimenti/${procedimentoId}/atti`, data, cb),
    approvaAtto: (procedimentoId, attoId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/${attoId}/approva`, {}, cb),
    inviaFirmaAtto: (procedimentoId, attoId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/${attoId}/invia-firma`, {}, cb),
    pubblicaAtto: (procedimentoId, attoId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/${attoId}/pubblica`, {}, cb),

    // Generazione atti precompilati
    generaNominaRup: (procedimentoId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/nomina-rup`, {}, cb),
    generaAvvioProcedimento: (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/avvio-procedimento`, data, cb),
    generaComunicazioneAvvio: (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/comunicazione-avvio`, data, cb),
    generaApprovazioneDip: (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/approvazione-dip`, data, cb),
    generaSelezioneProposte: (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/selezione-proposte`, data, cb),
    generaRichiestaIntegrazioni: (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/atti/richiesta-integrazioni`, data, cb),
    generaApprovazioneProgTriennale: (data, cb) =>
      post(`${BASE}/atti/approvazione-prog-triennale`, data, cb),
    scaricaPdfAtto: (procedimentoId, attoId) =>
      `${BASE}/procedimenti/${procedimentoId}/atti/${attoId}/pdf`,

    // Avviso Pubblico PPP (art. 193 co. 16 D.lgs 36/2023)
    listAvvisiPubblici:      (params, cb) => {
      const qs = params?.procedimentoId ? `?procedimentoId=${params.procedimentoId}` : '';
      return get(`${BASE}/avvisi-pubblici${qs}`, cb);
    },
    getAvvisoPubblico:       (id, cb) => get(`${BASE}/avvisi-pubblici/${id}`, cb),
    creaAvvisoPubblico:      (data, cb) => post(`${BASE}/avvisi-pubblici`, data, cb),
    aggiornaAvvisoPubblico:  (id, data, cb) => put(`${BASE}/avvisi-pubblici/${id}`, data, cb),
    pubblicaAvvisoPubblico:  (id, data, cb) => post(`${BASE}/avvisi-pubblici/${id}/pubblica`, data, cb),
    eliminaAvvisoPubblico:   (id, cb) => del(`${BASE}/avvisi-pubblici/${id}`, cb),

    // Allegati avviso
    listAllegatiAvviso:   (avvisoId, cb) => get(`${BASE}/avvisi-pubblici/${avvisoId}/allegati`, cb),
    uploadAllegatoAvviso: (avvisoId, formData, cb) =>
      request(`${BASE}/avvisi-pubblici/${avvisoId}/allegati`, {
        method: 'POST', body: formData, type: 'file_upload', ...cb,
      }),
    eliminaAllegatoAvviso: (avvisoId, allegatoId, cb) =>
      del(`${BASE}/avvisi-pubblici/${avvisoId}/allegati/${allegatoId}`, cb),

    // Revoca e versioning avviso
    revocaAvviso: (avvisoId, formData, cb) =>
      request(`${BASE}/avvisi-pubblici/${avvisoId}/revoca`, {
        method: 'POST', body: formData, type: 'file_upload', ...cb,
      }),
    creaVersioneAvviso: (avvisoId, cb) =>
      post(`${BASE}/avvisi-pubblici/${avvisoId}/crea-versione`, {}, cb),

    // Registro operatori avviso
    listOperatoriAvviso:    (avvisoId, cb) => get(`${BASE}/avvisi-pubblici/${avvisoId}/operatori`, cb),
    registraOperatoreAvviso: (avvisoId, data, cb) => post(`${BASE}/avvisi-pubblici/${avvisoId}/operatori`, data, cb),

    // Richieste di integrazione documentale
    listRichiesteAvviso:    (avvisoId, cb) => get(`${BASE}/avvisi-pubblici/${avvisoId}/richieste`, cb),
    creaRichiestaAvviso:    (avvisoId, data, cb) => post(`${BASE}/avvisi-pubblici/${avvisoId}/richieste`, data, cb),
    pubblicaRispostaAvviso: (avvisoId, richiestaId, data, cb) => {
      const url = `${BASE}/avvisi-pubblici/${avvisoId}/richieste/${richiestaId}/pubblica-risposta`;
      if (data.fileRisposta) {
        const fd = new FormData();
        fd.append('testoRisposta', data.testoRisposta);
        fd.append('dataRisposta', data.dataRisposta);
        fd.append('fileRisposta', data.fileRisposta);
        return request(url, { method: 'POST', body: fd, type: 'file_upload', ...cb });
      }
      return post(url, data, cb);
    },

    // Operatore — avvisi pubblici (auth richiesta, ruolo operatore_economico)
    listAvvisiOperatore:    (cb) => get('/api/v1/operatore/avvisi', cb),
    getAvvisoOperatore:     (avvisoId, cb) => get(`/api/v1/operatore/avvisi/${avvisoId}`, cb),
    registraInteresseAvviso: (avvisoId, cb) => post(`/api/v1/operatore/avvisi/${avvisoId}/interesse`, {}, cb),
    creaRichiestaDocAvviso: (avvisoId, data, cb) => post(`/api/v1/operatore/avvisi/${avvisoId}/richieste`, data, cb),

    // Pubblico — avvisi pubblici (no auth)
    listAvvisiPublic: (cb) => get('/api/v1/public/avvisi', cb),
    getAvvisoPublic:  (uuid, cb) => get(`/api/v1/public/avvisi/${uuid}`, cb),
    urlDownloadAllegatoPublic: (uuid, allegatoId) =>
      `/api/v1/public/avvisi/${uuid}/allegati/${allegatoId}/download`,
    urlDownloadAllegatoOperatore: (avvisoId, allegatoId) =>
      `/api/v1/operatore/avvisi/${avvisoId}/allegati/${allegatoId}/download`,

    // DIP — Documento di Indirizzo alla Progettazione (art. 3 Allegato I.7 D.lgs 36/2023)
    listDip:            (params, cb) => {
      const qs = params?.procedimentoId ? `?procedimentoId=${params.procedimentoId}` : '';
      return get(`${BASE}/dip${qs}`, cb);
    },
    getDip:             (id, cb) => get(`${BASE}/dip/${id}`, cb),
    creaDip:            (data, cb) => post(`${BASE}/dip`, data, cb),
    aggiornaDip:        (id, data, cb) => put(`${BASE}/dip/${id}`, data, cb),
    inviaInRevisioneDip:(id, cb) => post(`${BASE}/dip/${id}/invia-in-revisione`, {}, cb),
    approvaDip:         (id, data, cb) => post(`${BASE}/dip/${id}/approva`, data, cb),
    eliminaDip:         (id, cb) => del(`${BASE}/dip/${id}`, cb),
    urlDeliberaDip:     (id) => `${process.env.REACT_APP_API_URL}${BASE}/dip/${id}/delibera-pdf`,
    coerenzaProposta:   (dipId, propostaId, cb) => get(`${BASE}/dip/${dipId}/coerenza-proposta/${propostaId}`, cb),

    // RUP — assegnaRup nomina sul procedimento; listUtentiRup è definita sopra
    // (accetta enteId per il filtro associati, vincolo Multi-Tenant v3).
    assegnaRup:       (procedimentoId, rupUuid, cb) =>
      request(`${BASE}/procedimenti/${procedimentoId}/rup`, {
        method: 'PATCH', body: JSON.stringify({ rupUuid }), ...cb,
      }),

    // Dashboard stats aggregate
    getDashboardStats:      (cb) => get('/api/v1/ppp/dashboard/stats', cb),
    getDashboardKanban:     (cb) => get('/api/v1/ppp/dashboard/kanban', cb),
    getScadenzeImminenti:   (giorni, cb) => get(`/api/v1/ppp/dashboard/scadenze-imminenti?giorni=${giorni}`, cb),
    getKpiFasi:             (cb) => get('/api/v1/ppp/dashboard/kpi-fasi', cb),

    // Procedimenti assegnati (vista RUP)
    listProcedimentiAssegnati: (cb) => get(`${BASE}/assegnati`, cb),

    // Programmazione Triennale PPP (art. 175 D.lgs 36/2023)
    listProgrammi:       (cb) => get(`${BASE}/programmazione/programmi`, cb),
    getProgramma:        (id, cb) => get(`${BASE}/programmazione/programmi/${id}`, cb),
    creaProgramma:       (data, cb) => post(`${BASE}/programmazione/programmi`, data, cb),
    aggiornaProgramma:   (id, data, cb) => put(`${BASE}/programmazione/programmi/${id}`, data, cb),
    approvaProgramma:    (id, data, cb) => post(`${BASE}/programmazione/programmi/${id}/approva`, data, cb),
    pubblicaProgramma:   (id, data, cb) => post(`${BASE}/programmazione/programmi/${id}/pubblica`, data, cb),

    // PDF programma
    urlDeliberaPdf:     (programmaId) => `${BASE}/programmazione/programmi/${programmaId}/delibera-pdf`,

    // Interventi
    creaIntervento:     (programmaId, data, cb) =>
      post(`${BASE}/programmazione/programmi/${programmaId}/interventi`, data, cb),
    aggiornaIntervento: (programmaId, interventoId, data, cb) =>
      put(`${BASE}/programmazione/programmi/${programmaId}/interventi/${interventoId}`, data, cb),
    eliminaIntervento:  (programmaId, interventoId, cb) =>
      del(`${BASE}/programmazione/programmi/${programmaId}/interventi/${interventoId}`, cb),

    // Fase 8 — Schema di Valutazione Comparativa (art. 193, co. 5 D.lgs 36/2023)
    listSchemiValutazione:   (procedimentoId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/schemi-valutazione`, cb),
    getSchemaValutazione:    (procedimentoId, schemaId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/schemi-valutazione/${schemaId}`, cb),
    urlSchemaPdf: (procedimentoId, schemaId) =>
      `${process.env.REACT_APP_API_URL ?? ''}${BASE}/procedimenti/${procedimentoId}/schemi-valutazione/${schemaId}/pdf`,
    creaSchemaValutazione:   (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/schemi-valutazione`, data, cb),
    aggiornaSchemaValutazione: (procedimentoId, schemaId, data, cb) =>
      put(`${BASE}/procedimenti/${procedimentoId}/schemi-valutazione/${schemaId}`, data, cb),
    approvaSchemaValutazione:  (procedimentoId, schemaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/schemi-valutazione/${schemaId}/approva`, data, cb),

    // Fase 9 — Selezione e Valutazione Comparativa Finale (art. 193, co. 5-6 D.lgs 36/2023)
    listValutazioniComparative: (procedimentoId, schemaValutazioneId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/valutazioni-comparative?schemaValutazioneId=${schemaValutazioneId}`, cb),
    upsertPunteggio: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/punteggio`, data, cb),

    listVerbaliValutazione:  (procedimentoId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/verbali-valutazione`, cb),
    getVerbaleValutazione:   (procedimentoId, verbaleId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/verbali-valutazione/${verbaleId}`, cb),
    creaVerbaleValutazione:  (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/verbali-valutazione`, data, cb),
    aggiornaVerbaleValutazione: (procedimentoId, verbaleId, data, cb) =>
      put(`${BASE}/procedimenti/${procedimentoId}/verbali-valutazione/${verbaleId}`, data, cb),
    approvaVerbaleValutazione: (procedimentoId, verbaleId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/verbali-valutazione/${verbaleId}/approva`, data, cb),
    pubblicaEsitoVerbale: (procedimentoId, verbaleId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/verbali-valutazione/${verbaleId}/pubblica-esito`, data, cb),
    invitaModificheVerbale: (procedimentoId, verbaleId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/verbali-valutazione/${verbaleId}/invita-modifiche`, data, cb),

    // Fase 10 — Richiesta Integrazioni Documentali (art. 193, co. 6 D.lgs 36/2023)
    listIntegrazioni: (procedimentoId, propostaId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni`, cb),
    creaRichiestaIntegrazione: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni`, data, cb),
    registraRispostaIntegrazione: (procedimentoId, propostaId, richiestaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni/${richiestaId}/registra-risposta`, data, cb),
    respingiIntegrazione: (procedimentoId, propostaId, richiestaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni/${richiestaId}/respingi`, data, cb),

    // Fase 10 — Conferenza di Servizi Preliminare (art. 14, co. 3 L. 241/1990)
    listConferenzeServizi: (procedimentoId, propostaId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/conferenze-servizi`, cb),
    attivaConferenzaServizi: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/conferenze-servizi`, data, cb),
    chiudiConferenzaServizi: (procedimentoId, propostaId, conferenzaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/conferenze-servizi/${conferenzaId}/chiudi`, data, cb),

    // Fase 11 — Valutazione Integrazioni Documentali (art. 193, co. 6 D.lgs 36/2023)
    listValutazioniIntegrazioni: (procedimentoId, propostaId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/valutazioni-integrazioni`, cb),
    getValutazioneIntegrazione: (procedimentoId, propostaId, valutazioneId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/valutazioni-integrazioni/${valutazioneId}`, cb),
    creaValutazioneIntegrazione: (procedimentoId, propostaId, richiestaId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni/${richiestaId}/valutazioni`, {}, cb),
    aggiornaChecklistVoce: (procedimentoId, propostaId, valutazioneId, data, cb) =>
      patch(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/valutazioni-integrazioni/${valutazioneId}/checklist`, data, cb),
    completaValutazioneIntegrazione: (procedimentoId, propostaId, valutazioneId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/valutazioni-integrazioni/${valutazioneId}/completa`, data, cb),

    // Fase 12 — Provvedimento di Ammissione o Respingimento (art. 193, co. 6 D.lgs 36/2023)
    listProvvedimentiFase12: (procedimentoId, propostaId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/provvedimenti-fase12`, cb),
    getProvvedimentoFase12: (procedimentoId, propostaId, provvedimentoId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/provvedimenti-fase12/${provvedimentoId}`, cb),
    creaProvvedimentoFase12: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/provvedimenti-fase12`, data, cb),
    inviaComunicazioneProvvedimento: (procedimentoId, propostaId, provvedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/provvedimenti-fase12/${provvedimentoId}/invia-comunicazione`, data, cb),
    pubblicaProvvedimentoFase12: (procedimentoId, propostaId, provvedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/provvedimenti-fase12/${provvedimentoId}/pubblica`, data, cb),
    allegaDocumentoProvvedimento: (procedimentoId, propostaId, provvedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/provvedimenti-fase12/${provvedimentoId}/allega-documento`, data, cb),
    rimuoviDocumentoProvvedimento: (procedimentoId, propostaId, provvedimentoId, cb) =>
      del(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/provvedimenti-fase12/${provvedimentoId}/rimuovi-documento`, cb),

    // Fase 13 — Integrazione e Approvazione PFTE (art. 193, commi 7-8 + art. 38 D.lgs 36/2023)
    avviaIntegrazionePfte: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte`, data, cb),
    getIntegrazionePfte: (procedimentoId, propostaId, integrazioneId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}`, cb),
    aggiornaChecklistPfte: (procedimentoId, propostaId, integrazioneId, data, cb) =>
      patch(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/checklist`, data, cb),
    verificaCompletezzaPfte: (procedimentoId, propostaId, integrazioneId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/verifica-completezza`, data, cb),
    approvaIntegrazionePfte: (procedimentoId, propostaId, integrazioneId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/approva`, data, cb),
    listPareriFase13: (procedimentoId, propostaId, integrazioneId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/pareri`, cb),
    registraParereFase13: (procedimentoId, propostaId, integrazioneId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/pareri`, data, cb),
    aggiornaStatoParereFase13: (procedimentoId, propostaId, integrazioneId, parereId, data, cb) =>
      patch(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/pareri/${parereId}/stato`, data, cb),
    listConferenzeF13: (procedimentoId, propostaId, integrazioneId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/conferenze-f13`, cb),
    attivaConferenzaF13: (procedimentoId, propostaId, integrazioneId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/conferenze-f13`, data, cb),
    chiudiConferenzaF13: (procedimentoId, propostaId, integrazioneId, conferenzaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/integrazioni-pfte/${integrazioneId}/conferenze-f13/${conferenzaId}/chiudi`, data, cb),

    // Fase 14 — Documentazione di Gara
    getDocumentazioneGara: (procedimentoId, propostaId, documentazioneId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/documentazioni-gara/${documentazioneId}`, cb),
    avviaDocumentazioneGara: (procedimentoId, propostaId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/documentazioni-gara`, data, cb),
    aggiornaDocumentoGara: (procedimentoId, propostaId, documentazioneId, documentoId, data, cb) =>
      patch(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/documentazioni-gara/${documentazioneId}/documenti/${documentoId}`, data, cb),
    finalizzaDocumentazioneGara: (procedimentoId, propostaId, documentazioneId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/documentazioni-gara/${documentazioneId}/finalizza`, {}, cb),
    esportaDocumentazioneGara: (procedimentoId, propostaId, documentazioneId, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/proposte/${propostaId}/documentazioni-gara/${documentazioneId}/esporta`, {}, cb),


    // Enti del RUP autenticato (solo enti accettati)
    listMieiEnti: (cb) => get('/api/v1/pa/miei-enti', cb),
    // Ente dell'utente PA autenticato (dirigente, amm_ente, ecc.)
    getMioEnte: (cb) => get('/api/v1/pa/mio-ente', cb),

    // Enti (PA registrate)
    listEnti:      (cb) => get(`${BASE}/enti`, cb),
    creaEnte:      (data, cb) => post(`${BASE}/enti`, data, cb),
    aggiornaEnte:  (id, data, cb) => put(`${BASE}/enti/${id}`, data, cb),
    eliminaEnte:   (id, cb) => del(`${BASE}/enti/${id}`, cb),
    // Inviti ente (superadmin)
    listInvitiEnte:  (enteId, cb) => get(`${BASE}/enti/${enteId}/inviti`, cb),
    creaInvitoEnte:  (enteId, data, cb) => post(`${BASE}/enti/${enteId}/inviti`, data, cb),
    revocaInvitoEnte: (enteId, invitoId, cb) => del(`${BASE}/enti/${enteId}/inviti/${invitoId}`, cb),

    // Company / Operatori Economici (superadmin)
    listCompanies:        (cb) => get(`${BASE}/companies`, cb),
    getCompany:           (id, cb) => get(`${BASE}/companies/${id}`, cb),
    listMembriCompany:    (id, cb) => get(`${BASE}/companies/${id}/membri`, cb),
    aggiornaCompany:      (id, data, cb) => put(`${BASE}/companies/${id}`, data, cb),
    invitaMembroCompany:  (id, data, cb) => post(`${BASE}/companies/${id}/inviti`, data, cb),
    revocaInvitoCompany:  (id, invitoId, cb) => del(`${BASE}/companies/${id}/inviti/${invitoId}`, cb),
    rimuoviMembroCompany: (id, membroId, cb) => del(`${BASE}/companies/${id}/membri/${membroId}`, cb),

    // Richieste di accesso RUP/Ente (superadmin) — sotto /api/v1/pa
    listRichiesteRup: (stato, tipo, cb) => {
      const qs = new URLSearchParams();
      if (stato) qs.append('stato', stato);
      if (tipo)  qs.append('tipo', tipo);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      return get(`/api/v1/pa/richieste-rup${q}`, cb);
    },
    approvaRichiestaRup: (id, cb) =>
      post(`/api/v1/pa/richieste-rup/${id}/approva`, {}, cb),
    rifiutaRichiestaRup: (id, data, cb) =>
      post(`/api/v1/pa/richieste-rup/${id}/rifiuta`, data, cb),

    // Notifiche interne PPP
    listNotifiche: (params, cb) => {
      const qs = new URLSearchParams();
      if (params?.nonLette) qs.append('non_lette', '1');
      if (params?.perPage)  qs.append('per_page', params.perPage);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      return get(`${BASE}/notifiche${q}`, cb);
    },
    contaNotifiche:      (cb) => get(`${BASE}/notifiche/conta`, cb),
    marcaNotificaLetta:  (id, cb) => put(`${BASE}/notifiche/${id}/letta`, {}, cb),
    marcaTutteNotificheLette: (cb) => put(`${BASE}/notifiche/tutte-lette`, {}, cb),

    // Trasparenza — Log pubblicazioni obbligatorie (art. 28, art. 35 D.lgs 36/2023, D.lgs 33/2013)
    listPubblicazioni:      (procedimentoId, params, cb) => {
      const qs = new URLSearchParams();
      if (params?.fase)               qs.append('fase', params.fase);
      if (params?.tipoPubblicazione)  qs.append('tipo_pubblicazione', params.tipoPubblicazione);
      if (params?.canale)             qs.append('canale', params.canale);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      return get(`${BASE}/procedimenti/${procedimentoId}/trasparenza${q}`, cb);
    },
    getChecklistPubblicazioni: (procedimentoId, cb) =>
      get(`${BASE}/procedimenti/${procedimentoId}/trasparenza/checklist`, cb),
    registraPubblicazione: (procedimentoId, data, cb) =>
      post(`${BASE}/procedimenti/${procedimentoId}/trasparenza`, data, cb),
    eliminaPubblicazione: (procedimentoId, logId, cb) =>
      del(`${BASE}/procedimenti/${procedimentoId}/trasparenza/${logId}`, cb),
  };
}
